import React from 'react';
import PropTypes from 'prop-types';
import { Editor } from '@tinymce/tinymce-react';

import uploadImage from '../../edlio-api/images/uploadImage';
import { cmsColors } from '../../edlio-js/constants';

/**
 * RichTextEditor provides a configured TinyMCE editor.
 *
 * Javascript Dependencies
 * @requires js:common/tinymce/tinymce.min
 */
export default class RichTextEditor extends React.Component {
  filePickerCallback(callback, value, meta) {
    if (meta.filetype === 'image') {
      const fileInputEl = document.createElement('input');

      fileInputEl.setAttribute('type', 'file');
      fileInputEl.setAttribute('id', 'fileTMCE');
      fileInputEl.setAttribute('accept', 'image/*');
      fileInputEl.style.display = 'none';

      document.body.appendChild(fileInputEl);

      fileInputEl.addEventListener('change', async (evt) => {
        const { files } = evt.target;

        let basePath = window.edlio.public_site_uri;

        if (basePath.endsWith('/')) {
          basePath = basePath.slice(0, -1);
        }

        // add uploading status
        document.body.setAttribute('data-loading', 'uploading...');

        try {
          const uploadResponse = await uploadImage(files[0]);

          if (uploadResponse.length === 1) {
            callback(`${basePath}${uploadResponse[0].url}`);
          }

          document.body.removeAttribute('data-loading');
        } catch (err) {
          document.body.setAttribute('data-loading', 'failed, please try again later');

          setTimeout(() => {
            document.body.removeAttribute('data-loading');
          }, 3000);
        }

        if (fileInputEl && fileInputEl.parentNode) {
          fileInputEl.parentNode.removeChild(fileInputEl);
        }
      });

      fileInputEl.click();
    }
  }

  get toolbar() {
    const { toolbarOptions } = this.props;

    if (!toolbarOptions) {
      return `undo redo | 
      bold italic |
      alignleft aligncenter alignright |
      bullist numlist |
      outdent indent |
      forecolor | 
      link |
      code |
      removeformat |
      fontsizeselect |
      styleselect |
      table |
      image`;
    }

    return toolbarOptions;
  }

  render() {
    const {
      label,
      value,
      initialValue,
      margin,
      height,
      disabled,
      autoResize,
      stickyToolbar,
      hasError,
      onChange,
      onFocus,
      onBlur,
      onPaste,
      colorMap
    } = this.props;

    return (
      <div style={{ margin }}>
        {label &&
          <label
            style={{
              color: hasError ? cmsColors.alertColor : null,
              fontWeight: hasError ? 'bold' : null
            }}
          >
            {label}
          </label>
        }

        <div style={{ position: 'relative' }}>
          {/* in addition to using Editor's disabled prop which doesn't look 'disabled' enough */}
          <div
            style={{
              position: 'absolute',
              height: '100%',
              width: '100%',
              zIndex: '1',
              background: disabled ? 'rgba(0,0,0,0.1)' : null,
              pointerEvents: disabled ? null : 'none' // 'none' allows pass-through of click
            }}
          />

          <Editor
            init={{
              menubar: false,
              elementpath: false,
              branding: false,
              contextmenu: false,
              ...colorMap && { color_map: colorMap },
              height,
              /*
               * Must be at least 200 to properly initialize with autoresize enabled.
               * Also prevents minimizing so much that you can't click the editor input
               */
              min_height: 200,
              plugins: [
                'advlist', // https://www.tiny.cloud/docs/plugins/advlist/
                'autolink', // https://www.tiny.cloud/docs/plugins/autolink/
                'link', // https://www.tiny.cloud/docs/plugins/link/
                'code', // https://www.tiny.cloud/docs/plugins/code/
                'lists', // https://www.tiny.cloud/docs/plugins/lists/
                'image', // https://www.tiny.cloud/docs/plugins/image/
                'imagetools', // https://www.tiny.cloud/docs/plugins/imagetools/
                'table', // https://www.tiny.cloud/docs/plugins/table/
                'edlio_image', // requires alt text
                autoResize && 'autoresize', // https://www.tiny.cloud/docs/plugins/autoresize/
                stickyToolbar && 'edlio_stickytoolbar' // custom edlio plugin for sticky toolbar
              ].filter(Boolean).join(' '),
              toolbar: this.toolbar,
              toolbar_mode: 'wrap',
              toolbar_drawer: 'wrap',
              file_picker_callback: this.filePickerCallback,
              file_picker_types: 'image',
              style_formats: [
                { title: 'Underline', icon: 'underline', format: 'underline' },
                { title: 'Strikethrough', icon: 'strike-through', format: 'strikethrough' },
                { title: 'Superscript', icon: 'superscript', format: 'superscript' },
                { title: 'Subscript', icon: 'subscript', format: 'subscript' },
                { title: 'Code', icon: 'sourcecode', format: 'code' },
                {
                  title: 'Headers',
                  items: [
                    { title: 'Header 2', format: 'h2' },
                    { title: 'Header 3', format: 'h3' },
                    { title: 'Header 4', format: 'h4' },
                    { title: 'Header 5', format: 'h5' },
                    { title: 'Header 6', format: 'h6' }
                  ]
                },
                {
                  title: 'Blocks',
                  items:
                    [
                      { title: 'Paragraph', format: 'p' },
                      { title: 'Div', format: 'div' }
                    ]
                }
              ],
              setup: (editor) => {
                editor.ui.registry.addContextToolbar('image-menu', {
                  // eslint-disable-next-line object-shorthand
                  predicate: function (node) {
                    return node.nodeName.toLowerCase() === 'img'
                  },
                  position: 'node',
                  scope: 'node',
                  items: 'link image'
                });
              }
            }}
            initialValue={initialValue}
            value={value}
            disabled={disabled}
            onEditorChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onPaste={onPaste}
          />
        </div>
      </div>
    )
  }
}

RichTextEditor.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  initialValue: PropTypes.string,
  margin: PropTypes.string,
  height: PropTypes.number,
  disabled: PropTypes.bool,
  /**
   * Causes editor height to adjust automatically based on the content
   */
  autoResize: PropTypes.bool,
  /**
   * Causes toolbar to 'stick' to the bottom of the edlio site header while
   * editor is in view, but you've scrolled past the editor's toolbar
   */
  stickyToolbar: PropTypes.bool,
  hasError: PropTypes.bool,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onPaste: PropTypes.func,
  toolbarOptions: PropTypes.string,
  colorMap: PropTypes.array
};

RichTextEditor.defaultProps = {
  height: 250
}



// WEBPACK FOOTER //
// ./src/main/webapps/common/react/components/rich-text-editor.js